import { lazy, type ComponentType } from 'react'

/**
 * A lazily-loaded route that survives a deployment.
 *
 * Every route below the shell is code-split, which means navigating to one
 * fetches `/assets/Admin-<hash>.js` at the moment it is opened. The hash is
 * part of the filename, so a deploy replaces the whole `dist` and the previous
 * build's chunks stop existing — on the server *and*, once the new service
 * worker activates and Workbox cleans outdated caches, on the device.
 *
 * A tab that was opened before the deploy is still running the old build's
 * module graph. It asks for the old filename, gets a 404, `import()` rejects
 * with "Failed to fetch dynamically imported module", `React.lazy` throws it,
 * and the error boundary shows a crash page — for a screen that is perfectly
 * fine and one reload away.
 *
 * The floor keeps this application open all day. Anyone whose tab outlived a
 * release met that page, and the message they got — "the error was recorded" —
 * was actively misleading, because nothing was wrong with the page.
 *
 * ## Reloading exactly once
 *
 * `index.html` is served `no-cache` and revalidates, so a plain reload picks up
 * the new build's hashed filenames. The flag is what stops it becoming a loop:
 * a chunk that genuinely cannot be fetched — a device that went offline
 * mid-download, an asset a proxy is blocking — would otherwise reload forever
 * and take the crash page with it. One attempt, then the failure is allowed
 * through to the boundary, which now says what it means.
 *
 * The flag lives in `sessionStorage` rather than a module variable because the
 * reload destroys module state, which is the one thing it must survive. It is
 * cleared by the first chunk that loads successfully, so the *next* deploy gets
 * its own attempt.
 */
const RELOADED = 'kaprolean:reloaded-for-chunk'

/** True for the several ways a browser words "that module would not load". */
export function isChunkLoadFailure(error: unknown): boolean {
  if (!(error instanceof Error)) return false

  const message = error.message.toLowerCase()

  return (
    // Chrome, Edge
    message.includes('failed to fetch dynamically imported module') ||
    // Firefox
    message.includes('error loading dynamically imported module') ||
    // Safari
    message.includes('importing a module script failed') ||
    // Vite's own preload helper, which fails before the import does
    message.includes('unable to preload css') ||
    message.includes('dynamic import')
  )
}

/**
 * The recovery itself, separated from `lazy()` so it can be tested.
 *
 * `lazy` swallows the loader into React's internal state and only surfaces the
 * result through a render — which makes "did this reload, and did it reload
 * exactly once" a question about a component tree rather than about a promise.
 * The promise is the whole behaviour, so that is what is exported.
 */
export function loadWithStaleBuildRecovery<T>(load: () => Promise<T>): Promise<T> {
  return load()
    .then((module) => {
      // Something loaded, so whatever went wrong is over. Cleared here rather
      // than on reload so a build that is broken in a way a reload cannot fix
      // does not get a fresh attempt on every navigation.
      sessionStorage.removeItem(RELOADED)

      return module
    })
    .catch((error: unknown) => {
      if (!isChunkLoadFailure(error) || sessionStorage.getItem(RELOADED)) {
        throw error
      }

      sessionStorage.setItem(RELOADED, '1')
      window.location.reload()

      /*
       * Never settles, and that is deliberate: the page is being torn down, and
       * resolving would render a component into a document that is about to be
       * replaced. Suspense keeps showing its fallback until the reload takes
       * over, which reads as a slow navigation rather than as an error.
       */
      return new Promise<T>(() => {})
    })
}

/*
 * `ComponentType<any>` rather than a narrower bound, matching React's own
 * signature for `lazy`. A route component's props are supplied by the router,
 * never by a caller here, so there is nothing for a stricter type to protect —
 * and `ComponentType<never>` rejects every class component outright.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyRoute<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
) {
  return lazy(() => loadWithStaleBuildRecovery(load))
}
